// Pair-strength calculator math — ported verbatim from the article
// (PairMaker/umag.html). This is the same B3d formula the optimizer and
// stats pages use, but kept standalone here because the article's calculator
// supports BOTH the illustrative 1–5 "score" mode and the real "elo" mode,
// and works on an ad-hoc 4-player cohort rather than the rated roster.

export type CalcMode = 'score' | 'elo';

export interface CalcPlayer {
  sz: number; // szerva / serve
  a: number; // alapjáték / baseline
  r: number; // röpte / net-volley
  fast: number; // gyors
  balanced: number; // kiegyensúlyozott
  cunning: number; // ravasz
}

export type PlayerLabel = 'A' | 'B' | 'C' | 'D';
export const PLAYER_LABELS: PlayerLabel[] = ['A', 'B', 'C', 'D'];

export const SCALE_MAX = 5;
export const ELO_MIN = 200;
export const ELO_MAX = 3400;
export const ELO_STEP = 10;

// Illustrative presets — different stylistic archetypes, tuned per scale.
export const PRESET_ELO: Record<PlayerLabel, CalcPlayer> = {
  A: { sz: 1300, a: 1250, r: 1050, fast: 1300, balanced: 1200, cunning: 950 },
  B: { sz: 950, a: 1100, r: 1400, fast: 850, balanced: 1200, cunning: 1350 },
  C: { sz: 1050, a: 1200, r: 1100, fast: 1000, balanced: 1250, cunning: 1000 },
  D: { sz: 1250, a: 1100, r: 1050, fast: 1250, balanced: 950, cunning: 1200 },
};

export const PRESET_SCORE: Record<PlayerLabel, CalcPlayer> = {
  A: { sz: 5, a: 4, r: 3, fast: 1, balanced: 1, cunning: 0 },
  B: { sz: 3, a: 4, r: 5, fast: 0, balanced: 1, cunning: 1 },
  C: { sz: 3, a: 4, r: 4, fast: 0, balanced: 1, cunning: 0 },
  D: { sz: 4, a: 4, r: 3, fast: 1, balanced: 0, cunning: 1 },
};

export type Cohort = Record<PlayerLabel, CalcPlayer>;

export function clonePreset(p: Cohort): Cohort {
  return {
    A: { ...p.A },
    B: { ...p.B },
    C: { ...p.C },
    D: { ...p.D },
  };
}

export interface CohortRefs {
  sz: number;
  a: number;
  r: number;
  fast: number;
  balanced: number;
  cunning: number;
}

// ----- normalization -----
function normalizeScore(v: number): number {
  return v / SCALE_MAX;
}
function normalizeElo(v: number, ref: number): number {
  // Cohort-mean centered logistic: probability the player wins this skill
  // exchange against an average opponent in the cohort.
  return 1 / (1 + Math.pow(10, (ref - v) / 400));
}

// ----- pair-strength components (normalized skills in [0, 1]) -----
function compFirstServes(sz1: number, a1: number, r2: number): number {
  return (sz1 + a1 + 0.5) * (r2 + 1);
}
function compSecondServes(sz2: number, a2: number, r1: number): number {
  return (sz2 + a2 + 0.5) * (r1 + 1);
}
function compOppServes(a1: number, r1: number, a2: number, r2: number): number {
  return (a1 + 1) * (r2 + 1) + (a2 + 1) * (r1 + 1);
}

// Score-mode trait term: binary traits aggregated, penalties for full gaps.
function traitTermScore(p1: CalcPlayer, p2: CalcPlayer): number {
  const bonus =
    (p1.fast | 0) +
    (p2.fast | 0) +
    (p1.balanced | 0) +
    (p2.balanced | 0) +
    (p1.cunning | 0) +
    (p2.cunning | 0);
  let penalty = 0;
  if ((p1.fast | 0) === 0 && (p2.fast | 0) === 0) penalty += 1;
  if ((p1.balanced | 0) === 0 && (p2.balanced | 0) === 0) penalty += 1;
  return bonus - penalty;
}

interface NormTraits {
  fast: number;
  balanced: number;
  cunning: number;
}
// Elo-mode trait term: continuous saturation curves per trait.
function traitTermElo(p1n: NormTraits, p2n: NormTraits): number {
  const tFast = p1n.fast + p2n.fast;
  const tBal = p1n.balanced + p2n.balanced;
  const tCun = p1n.cunning + p2n.cunning;
  const fFast = 1 - Math.exp(-3 * tFast);
  const fBal = 1 - Math.exp(-2 * tBal);
  const fCun = 0.5 * tCun;
  return 2 * fFast + 2 * fBal + 1 * fCun;
}

export interface PairBreakdown {
  c1: number;
  c2: number;
  c3: number;
  traits: number;
  total: number;
}

export function pairBreakdown(
  mode: CalcMode,
  p1: CalcPlayer,
  p2: CalcPlayer,
  refs: CohortRefs,
): PairBreakdown {
  let n1: { sz: number; a: number; r: number };
  let n2: { sz: number; a: number; r: number };

  if (mode === 'score') {
    n1 = { sz: normalizeScore(p1.sz), a: normalizeScore(p1.a), r: normalizeScore(p1.r) };
    n2 = { sz: normalizeScore(p2.sz), a: normalizeScore(p2.a), r: normalizeScore(p2.r) };
  } else {
    n1 = {
      sz: normalizeElo(p1.sz, refs.sz),
      a: normalizeElo(p1.a, refs.a),
      r: normalizeElo(p1.r, refs.r),
    };
    n2 = {
      sz: normalizeElo(p2.sz, refs.sz),
      a: normalizeElo(p2.a, refs.a),
      r: normalizeElo(p2.r, refs.r),
    };
  }

  const c1 = compFirstServes(n1.sz, n1.a, n2.r);
  const c2 = compSecondServes(n2.sz, n2.a, n1.r);
  const c3 = compOppServes(n1.a, n1.r, n2.a, n2.r);

  let traits: number;
  if (mode === 'score') {
    traits = traitTermScore(p1, p2);
  } else {
    const t1: NormTraits = {
      fast: normalizeElo(p1.fast, refs.fast),
      balanced: normalizeElo(p1.balanced, refs.balanced),
      cunning: normalizeElo(p1.cunning, refs.cunning),
    };
    const t2: NormTraits = {
      fast: normalizeElo(p2.fast, refs.fast),
      balanced: normalizeElo(p2.balanced, refs.balanced),
      cunning: normalizeElo(p2.cunning, refs.cunning),
    };
    traits = traitTermElo(t1, t2);
  }

  const total = c1 + c2 + c3 + traits;
  return { c1, c2, c3, traits, total };
}

export function cohortRefs(players: Cohort): CohortRefs {
  const mean = (key: keyof CalcPlayer) =>
    (players.A[key] + players.B[key] + players.C[key] + players.D[key]) / 4;
  return {
    sz: mean('sz'),
    a: mean('a'),
    r: mean('r'),
    fast: mean('fast'),
    balanced: mean('balanced'),
    cunning: mean('cunning'),
  };
}

export interface PairRow extends PairBreakdown {
  a: PlayerLabel;
  b: PlayerLabel;
}

export function allPairs(mode: CalcMode, players: Cohort): PairRow[] {
  const refs = cohortRefs(players);
  const out: PairRow[] = [];
  for (let i = 0; i < 4; i++) {
    for (let j = i + 1; j < 4; j++) {
      const a = PLAYER_LABELS[i];
      const b = PLAYER_LABELS[j];
      out.push({ a, b, ...pairBreakdown(mode, players[a], players[b], refs) });
    }
  }
  return out;
}

export interface Partition {
  p1: PairRow;
  p2: PairRow;
  spread: number;
}

export function partitionsFromPairs(pairs: PairRow[]): Partition[] {
  const get = (a: PlayerLabel, b: PlayerLabel) =>
    pairs.find((p) => (p.a === a && p.b === b) || (p.a === b && p.b === a))!;
  const partitions: Partition[] = [
    { p1: get('A', 'B'), p2: get('C', 'D'), spread: 0 },
    { p1: get('A', 'C'), p2: get('B', 'D'), spread: 0 },
    { p1: get('A', 'D'), p2: get('B', 'C'), spread: 0 },
  ];
  partitions.forEach((part) => {
    part.spread = Math.abs(part.p1.total - part.p2.total);
  });
  partitions.sort((x, y) => x.spread - y.spread);
  return partitions;
}

// Hungarian number formatting: comma decimal separator.
export function fmt(n: number, digits = 2): string {
  return n.toFixed(digits).replace('.', ',');
}
